import React, { useState } from 'react';
import axios from 'axios';
import { Camera, FileUp, Loader2, Download, CheckCircle2, AlertTriangle, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { useToast } from '../../hooks/use-toast';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` } });
const COLS = [['firstName', 'First Name'], ['lastName', 'Last Name'], ['phone', 'Cell Phone'], ['email', 'Email'], ['dateOfBirth', 'Date of Birth'], ['preferredLanguage', 'Language']];

const FormatGuide = () => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2 text-sm" data-testid="import-format-guide">
    <p className="font-semibold text-slate-800">Best format for your printed list</p>
    <p className="text-slate-600">One patient per row, with these columns (in any order):</p>
    <div className="flex flex-wrap gap-1.5">
      {['First Name *', 'Last Name *', 'Cell Phone *', 'Email *', 'Date of Birth', 'Language'].map((c) => (
        <span key={c} className="px-2 py-0.5 rounded bg-white border text-xs font-medium">{c}</span>
      ))}
    </div>
    <ul className="text-xs text-slate-600 list-disc ml-5 space-y-0.5">
      <li>* Each patient needs a name and at least an email or cell phone (both is best).</li>
      <li>Print a patient report from your PMS (Dentrix, Eaglesoft, Open Dental…) or use our template.</li>
      <li>Photograph pages flat, in good light, filling the frame. Up to 10 pages / photos at once, or a scanned PDF.</li>
    </ul>
    <div className="flex gap-2 pt-1">
      <a href="/patient-list-template.csv" download className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:underline" data-testid="import-template-csv"><Download className="h-3.5 w-3.5" /> Download template (CSV / Excel)</a>
    </div>
  </div>
);

export const PatientListImportDialog = ({ open, onOpenChange, onImported }) => {
  const { toast } = useToast();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);

  const reset = () => { setRows(null); setResult(null); setBusy(''); };
  const upload = async (fileList) => {
    if (!fileList?.length) return;
    setBusy('AI is reading your list…');
    try {
      const fd = new FormData();
      [...fileList].slice(0, 10).forEach((f) => fd.append('files', f));
      const r = await axios.post(`${BACKEND}/api/patient-import/extract`, fd, auth());
      setRows(r.data.rows);
      if (!r.data.rows.length) toast({ title: 'No patients found', description: 'Try a clearer, well-lit photo.', variant: 'destructive' });
    } catch (e) { toast({ title: 'Could not read the list', description: e.response?.data?.detail || 'Try again', variant: 'destructive' }); }
    setBusy('');
  };
  const edit = (id, k, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [k]: v } : r)));
  const chosen = (rows || []).filter((r) => r.selected);
  const commit = async () => {
    setBusy('Importing…');
    try { const r = await axios.post(`${BACKEND}/api/patient-import/commit`, { rows: chosen }, auth()); setResult(r.data); onImported?.(); }
    catch (e) { toast({ title: 'Import failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' }); }
    setBusy('');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="patient-import-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-sky-600" /> Import Patient List (photo, scan or PDF)</DialogTitle></DialogHeader>
        {result ? (
          <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-5" data-testid="import-result">
            <p className="font-semibold text-emerald-900 flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> {result.added} patient(s) added to your patient list</p>
            {result.skipped.length > 0 && <ul className="mt-2 text-sm text-amber-800 list-disc ml-6">{result.skipped.map((s) => <li key={s}>{s}</li>)}</ul>}
            <Button className="mt-4" onClick={reset} data-testid="import-another-btn">Import another list</Button>
          </div>
        ) : !rows ? (
          <div className="grid md:grid-cols-2 gap-5">
            <FormatGuide />
            <div className="flex flex-col gap-3 justify-center">
              {busy ? <div className="flex flex-col items-center gap-3 py-10"><Loader2 className="h-10 w-10 animate-spin text-sky-600" /><p className="text-sm" data-testid="import-busy">{busy}</p></div> : (
                <>
                  <label className="flex items-center justify-center gap-2 h-16 rounded-xl bg-sky-600 text-white font-semibold cursor-pointer hover:bg-sky-700" data-testid="import-camera-btn">
                    <Camera className="h-5 w-5" /> Take photo(s)
                    <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="import-camera-input" />
                  </label>
                  <label className="flex items-center justify-center gap-2 h-16 rounded-xl border-2 border-dashed border-slate-300 font-semibold text-slate-700 cursor-pointer hover:bg-slate-50" data-testid="import-file-btn">
                    <FileUp className="h-5 w-5" /> Upload photos or scanned PDF
                    <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="import-file-input" />
                  </label>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="import-review">
            <p className="text-sm text-slate-600">AI found <b>{rows.length}</b> patients. Check each row, fix anything misread, then import. Yellow rows were hard to read; grey rows are already in PractCom and are skipped.</p>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-100"><tr><th className="p-2 w-8" />{COLS.map(([, l]) => <th key={l} className="p-2 text-left font-semibold">{l}</th>)}<th className="p-2" /></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className={`border-t ${r.duplicate ? 'bg-slate-100 text-slate-400' : r.unclear ? 'bg-amber-50' : ''}`} data-testid={`import-row-${r.id}`}>
                      <td className="p-2"><Checkbox checked={r.selected} onCheckedChange={(c) => edit(r.id, 'selected', c === true)} data-testid={`import-row-select-${r.id}`} /></td>
                      {COLS.map(([k]) => (
                        <td key={k} className="p-1"><Input value={r[k] || ''} onChange={(e) => edit(r.id, k, e.target.value)} className={`h-8 text-sm ${k === 'email' ? 'min-w-[260px]' : 'min-w-[110px]'}`} data-testid={`import-cell-${k}-${r.id}`} /></td>
                      ))}
                      <td className="p-2 text-xs whitespace-nowrap">
                        {r.duplicate ? 'Already a patient' : r.unclear ? <span className="text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />Check</span> : !(r.email || r.phone) ? <span className="text-rose-600">Needs email or cell</span> : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={reset} data-testid="import-restart-btn">Start over</Button>
              <Button className="ml-auto bg-emerald-600 hover:bg-emerald-700" disabled={!chosen.length || !!busy} onClick={commit} data-testid="import-commit-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Import {chosen.length} patient(s)
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PatientListImportDialog;
